// Modal de criação de nova despesa (lançamento manual / parcelado em Contas a Pagar).
// Fase 2A — layout estilo Conta Azul. Extraído de FinanceiroContasPagar.tsx (refactor topológico).
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Modal, Form, Input, InputNumber, Select, Card, Row, Col, DatePicker, Button, Flex, Segmented,
  Upload, App as AntApp, Typography, Alert,
} from 'antd';
import { PlusOutlined, PaperClipOutlined, EditOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { getToken, fmt } from './utils';
import { opcoesFornecedor, filtrarFornecedor, FornecedorSlim } from '../../utils/fornecedores';
import { faturaDaCompra, diaMes } from '../../utils/cartaoFatura';
import { OPCOES_FORMA_PAGAMENTO, DespesaInicial, CHAVES_AUXILIARES,
  acharCentroDoPedido } from '../../utils/despesaOpcoes';
import { gerarVencimentos, conferirVencimentos,
  resumoVencimentos } from '../../utils/vencimentosParcelas';
import type { RitmoVencimento } from '../../utils/vencimentosParcelas';
import { ModalNovoFornecedor } from './ModalNovoFornecedor';
import { ModalNovoCentroCusto } from './ModalNovoCentroCusto';
import { PessoaDaEquipe, useFichasDaEquipe } from './PessoaDaEquipe';
import { pedeAPessoa, pessoaParaEnviar, faltaEscolherPessoa, sugestaoDePessoa } from '../../utils/pessoaDaDespesa';

const { Text } = Typography;

// Lucas 2026-08-21: o nº de parcelas/repetições é digitado (era dropdown, travado
// em 37×). Teto de 120 = 10 anos de recorrência mensal — o backend cria um
// lançamento por parcela num laço, e o mesmo limite está em contasPagarAnexos.js.
const MAX_PARCELAS = 120;

// Fase 2A — Modal Nova Despesa com layout estilo Conta Azul
export function ModalNovaDespesa({ onFechar, onCriou, inicial }: {
  onFechar: () => void;
  onCriou: (ids: string[]) => void;
  inicial?: DespesaInicial;
}) {
  const { message } = AntApp.useApp();
  const hoje = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    fornecedor_id: '' as string,
    fornecedor_original: '' as string,    // fallback texto livre
    data_competencia: hoje,
    descricao: '',
    valor: '',
    categoria_dre: '',
    grupo_dre: '',
    centro_custo: '',  // F-CP-cc: codigo_canonical do CC
    // condição: avista | parcelado (divide o valor) | recorrente (repete mensal)
    modo: 'avista' as 'avista' | 'parcelado' | 'recorrente',
    parcelas: 1 as number | null,   // null enquanto o campo está vazio (digitação)
    intervalo_dias: 30,
    data_vencimento: hoje,
    data_pagamento: '',
    metodo_pagamento: '',
    conta_financeira: '',
    conta_financeira_id: '',  // FZ 5.2 — FK da conta cadastrada (fluxo por banco)
    status: 'em_aberto' as 'em_aberto' | 'agendado' | 'pago' | 'cancelado',
    observacoes: '',
    // Só sobrescreve o que veio de fato: chave ausente no `inicial` não pode
    // apagar o default (data_competencia vazia estoura a sugestão de fatura).
    ...Object.fromEntries(Object.entries(inicial || {})
      .filter(([k, v]) => !CHAVES_AUXILIARES.includes(k)
        && v !== undefined && v !== null && v !== '')),
  });
  const [salvando, setSalvando] = useState(false);
  // Pessoa da equipe (05/10/2026): quem recebeu, quando a categoria é
  // remuneração. Enquanto a pessoa não mexe no campo, ele acompanha a sugestão
  // pelo nome do fornecedor; mexeu, a escolha dela vale.
  const { fichas, estado: estadoFichas, recarregar: recarregarFichas } = useFichasDaEquipe();
  const [pessoa, setPessoa] = useState('');
  const [pessoaMexida, setPessoaMexida] = useState(false);
  const [pessoaFaltando, setPessoaFaltando] = useState(false);
  // Anexo opcional (recibo/NF) — enviado após criar a despesa (usa o id retornado).
  const [anexos, setAnexos] = useState<UploadFile[]>([]);

  // Fornecedores (autocomplete)
  const [fornecedores, setFornecedores] = useState<any[]>([]);
  const [carregandoForns, setCarregandoForns] = useState(false);
  const [novoFornAberto, setNovoFornAberto] = useState(false);
  const [fornEditando, setFornEditando] = useState<FornecedorSlim | null>(null);
  const fornecedorSelecionado: FornecedorSlim | null =
    fornecedores.find((f: any) => f.id === form.fornecedor_id) || null;

  // Categorias DRE (dropdown)
  const [categoriasDre, setCategoriasDre] = useState<any[]>([]);
  // Centros de Custo (F-CP-cc)
  const [centrosCusto, setCentrosCusto] = useState<any[]>([]);
  const [novoCcAberto, setNovoCcAberto] = useState(false);
  // Contas bancárias — cadastro oficial (FZ 5.2); textos legados do CA como fallback
  const [contasFin, setContasFin] = useState<any[]>([]);
  const [contasDisponiveis, setContasDisponiveis] = useState<string[]>([]);
  // Cadastro de cartões (dia de fechamento/vencimento) — pro vencimento vir
  // sozinho quando a conta de pagamento é um cartão (Daniele, 31/08/2026).
  const [cartoes, setCartoes] = useState<any[]>([]);
  // O vencimento sugerido não briga com a pessoa: mexeu no campo, a sugestão
  // para de sobrescrever até ela trocar de conta de novo.
  const [vencAuto, setVencAuto] = useState(true);

  // CRONOGRAMA DAS PARCELAS (2026-09-18). Antes o servidor assumia 30 dias
  // corridos entre parcelas e o vencimento desgarrava do dia combinado já na
  // 3ª (10/09 → 10/10 → 09/11 → 09/12). Agora a lista de datas é explícita: o
  // pedido pode ter escolhido, e aqui dá para ajustar antes de confirmar.
  const [ritmo, setRitmo] = useState<RitmoVencimento>('mensal');
  const [vencimentos, setVencimentos] = useState<string[]>(
    Array.isArray(inicial?.vencimentos) ? inicial!.vencimentos! : []);
  // Veio do pedido: não regerar por cima do que a pessoa combinou lá.
  const [vencManual, setVencManual] = useState(!!inicial?.vencimentos?.length);
  const [datasAbertas, setDatasAbertas] = useState(false);

  // A conta de pagamento escolhida é um cartão? Liga pelo cartao_id do cadastro
  // de contas; cartão antigo sem o vínculo cai no casamento por nome (o nome do
  // cartão É o nome da conta no CA — é a chave dos matches do módulo Cartões).
  const contaSelecionada = contasFin.find((c: any) => c.id === form.conta_financeira_id) || null;
  const cartaoSelecionado = contaSelecionada?.tipo === 'cartao'
    ? (cartoes.find((k: any) => k.id === contaSelecionada.cartao_id)
       || cartoes.find((k: any) => (k.nome || '').toLowerCase() ===
            String(contaSelecionada.apelido || contaSelecionada.nome || '').toLowerCase())
       || null)
    : null;
  // A "data da compra" é a competência — é ela que decide em que fatura cai.
  const faturaSugerida = cartaoSelecionado
    ? faturaDaCompra(form.data_competencia, cartaoSelecionado)
    : null;

  const nParcelas = form.modo === 'avista' ? 1 : (form.parcelas || 1);
  // Cartão manda na data: a parcela cai na fatura, e o servidor recusa lista
  // explícita junto com o parcelamento de cartão. Fora isso, o cronograma vale.
  const cronogramaVale = !cartaoSelecionado && form.modo !== 'avista' && nParcelas >= 2;

  useEffect(() => {
    if (!cronogramaVale) return;
    if (vencManual && vencimentos.length === nParcelas) return;
    const gerado = gerarVencimentos(form.data_vencimento, nParcelas, ritmo, form.intervalo_dias);
    // Compara antes de gravar: array igual devolve a MESMA referência e o
    // efeito não se realimenta.
    setVencimentos(atual => (atual.join('|') === gerado.join('|') ? atual : gerado));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cronogramaVale, form.data_vencimento, nParcelas, ritmo, form.intervalo_dias, vencManual]);

  // Preenche o vencimento sozinho enquanto a pessoa não mexeu nele. Mexeu,
  // a sugestão continua VISÍVEL no texto abaixo do campo, mas para de gravar.
  // A forma de pagamento entrou sozinha? Só o que entrou sozinho sai sozinho.
  const [metodoAuto, setMetodoAuto] = useState(false);
  useEffect(() => {
    if (!faturaSugerida || !vencAuto) return;
    setForm(f => {
      if (f.data_vencimento === faturaSugerida.vencimento) return f;
      if (!f.metodo_pagamento) setMetodoAuto(true);
      return {
        ...f,
        data_vencimento: faturaSugerida.vencimento,
        metodo_pagamento: f.metodo_pagamento || 'Cartão',
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartaoSelecionado?.id, form.data_competencia, vencAuto]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        // Carrega fornecedores ativos
        setCarregandoForns(true);
        const [rF, rC, rB, rCC, rCF, rCard] = await Promise.all([
          axios.get(`${API_URL}/fornecedores?ativo=true&limit=2000`, { headers: { Authorization: token } }),
          axios.get(`${API_URL}/categorias`, { headers: { Authorization: token } }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/contas-pagar?limit=1`, { headers: { Authorization: token } }).catch(() => ({ data: { contas_disponiveis: [] } })),
          // TODOS os centros, inclusive estruturais/indiretas (Lucas 2026-07-16)
          axios.get(`${API_URL}/centros-custo?status=todos&simples=1`,
            { headers: { Authorization: token } }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/financeiro/contas`, { headers: { Authorization: token } }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/financeiro/cartoes`, { headers: { Authorization: token } }).catch(() => ({ data: { cartoes: [] } })),
        ]);
        const dF = rF.data.body ? JSON.parse(rF.data.body) : rF.data;
        setFornecedores(Array.isArray(dF) ? dF : []);
        const dC = rC.data.body ? JSON.parse(rC.data.body) : rC.data;
        setCategoriasDre(Array.isArray(dC) ? dC.filter((c: any) => c.linha_dre) : []);
        const dB = rB.data.body ? JSON.parse(rB.data.body) : rB.data;
        setContasDisponiveis(Array.isArray(dB?.contas_disponiveis) ? dB.contas_disponiveis : []);
        const dCC = rCC.data.body ? JSON.parse(rCC.data.body) : rCC.data;
        setCentrosCusto(Array.isArray(dCC) ? dCC : []);
        const dCF = rCF.data.body ? JSON.parse(rCF.data.body) : rCF.data;
        setContasFin(Array.isArray(dCF) ? dCF : []);
        const dCard = rCard.data.body ? JSON.parse(rCard.data.body) : rCard.data;
        setCartoes(Array.isArray(dCard?.cartoes) ? dCard.cartoes : []);
      } catch (e: any) {
        console.warn('Erro carregando metadados:', e.message);
      } finally { setCarregandoForns(false); }
    })();
  }, []);

  useEffect(() => {
    if (pessoaMexida || estadoFichas !== 'ok') return;
    setPessoa(sugestaoDePessoa(form.categoria_dre, form.fornecedor_original, fichas));
  }, [pessoaMexida, estadoFichas, fichas, form.categoria_dre, form.fornecedor_original]);

  async function salvar() {
    if (!form.descricao.trim() || !form.valor) {
      message.warning('Preencha descrição e valor'); return;
    }
    // Remuneração de gente da equipe sem dizer de quem: não entra no custo por
    // hora dos contratos nem na divisão da ficha. Cobra a escolha — "Não é da
    // equipe" também é resposta. Se a lista não carregou, não trava o salário.
    if (faltaEscolherPessoa({ linha: form.categoria_dre, escolha: pessoa, fichasCarregadas: estadoFichas === 'ok' })) {
      setPessoaFaltando(true);
      message.warning('Escolha a pessoa da equipe que recebeu — ou "Não é da equipe".');
      return;
    }
    if (!form.data_vencimento) { message.warning('Vencimento obrigatório'); return; }
    const valorNum = parseFloat(form.valor);
    if (isNaN(valorNum) || valorNum === 0) { message.warning('Valor inválido'); return; }
    if (form.modo !== 'avista' && (!form.parcelas || form.parcelas < 2)) {
      message.warning(form.modo === 'recorrente'
        ? 'Informe quantas vezes a despesa se repete (no mínimo 2).'
        : 'Informe em quantas parcelas dividir (no mínimo 2).');
      return;
    }
    if (form.parcelas && form.parcelas > MAX_PARCELAS) {
      message.warning(`No máximo ${MAX_PARCELAS} lançamentos de uma vez (${MAX_PARCELAS / 12} anos de recorrência mensal).`);
      return;
    }
    // Mesma régua do pré-voo do servidor: falhar aqui evita a viagem e a
    // mensagem sai em português na hora.
    if (cronogramaVale && vencimentos.length) {
      const erroCron = conferirVencimentos(vencimentos, nParcelas);
      if (erroCron) { message.warning(`Cronograma: ${erroCron}`); return; }
    }
    setSalvando(true);
    try {
      const token = await getToken();
      // F-CP-cc: monta centros_custo single-item se houver CC selecionado
      const ccSel = form.centro_custo
        ? centrosCusto.find((x: any) => x.codigo_canonical === form.centro_custo)
        : null;
      const centrosCustoPayload = ccSel ? [{
        centro: ccSel.codigo_canonical,
        valor: Math.abs(valorNum),
        cc_contrato_numero: ccSel.codigo_canonical,
        cc_contrato_titulo: ccSel.nome_completo || null,
      }] : [];
      const nLanc = form.modo === 'avista' ? 1 : (form.parcelas || 1);
      const rCria = await axios.post(`${API_URL}/contas-pagar`, {
        fornecedor_id: form.fornecedor_id || null,
        fornecedor_original: form.fornecedor_original || null,
        data_competencia: form.data_competencia,
        data_vencimento: form.data_vencimento,
        data_pagamento: form.data_pagamento || null,
        descricao: form.descricao.trim(),
        valor: valorNum,
        categoria_dre: form.categoria_dre || null,
        grupo_dre: form.grupo_dre || null,
        centros_custo: centrosCustoPayload,
        modo: form.modo,
        parcelas: nLanc,
        intervalo_dias: form.intervalo_dias,
        // Cronograma explícito. Com cartão fica de fora: quem manda é a fatura.
        vencimentos: cronogramaVale && vencimentos.length === nParcelas
          ? vencimentos : undefined,
        // Conta de pagamento é cartão → parcelas mês a mês, no dia do
        // vencimento (não de 30 em 30 dias). O servidor faz a conta certa.
        // O dia-alvo vai junto: a 1ª data pode chegar "recuada" (venc 31 numa
        // fatura de fevereiro = 28/02) e repetir esse dia erraria março/abril.
        parcelado_mensal: !!cartaoSelecionado,
        parcela_dia: cartaoSelecionado?.dia_vencimento || null,
        metodo_pagamento: form.metodo_pagamento || null,
        conta_financeira: form.conta_financeira || null,
        conta_financeira_id: form.conta_financeira_id || null,
        status: form.status,
        observacoes: form.observacoes || null,
        // vazio = não fala do assunto (o servidor tenta o nome, só em
        // remuneração de equipe); "Não é da equipe" = null; pessoa = o id
        profissional_id: pessoaParaEnviar(pessoa),
      }, { headers: { Authorization: token } });
      const dCria = rCria.data.body ? JSON.parse(rCria.data.body) : rCria.data;
      const ids: string[] = Array.isArray(dCria?.ids) ? dCria.ids : [];

      // Anexo (recibo/NF): sobe no PRIMEIRO lançamento criado (parcela 1).
      // Fluxo: upload-url presigned → PUT no S3 → confirma metadata.
      const arquivo = anexos[0]?.originFileObj as File | undefined;
      if (arquivo && ids[0]) {
        try {
          const { data: pre } = await axios.post(
            `${API_URL}/contas-pagar/${ids[0]}/anexos/upload-url`,
            { filename: arquivo.name, content_type: arquivo.type || 'application/octet-stream' },
            { headers: { Authorization: token } });
          const preData = pre.body ? JSON.parse(pre.body) : pre;
          await axios.put(preData.url, arquivo, {
            headers: { 'Content-Type': arquivo.type || 'application/octet-stream' } });
          await axios.post(`${API_URL}/contas-pagar/${ids[0]}/anexos`,
            { s3_key: preData.s3_key, nome: arquivo.name, mime: arquivo.type || null, tamanho_bytes: arquivo.size },
            { headers: { Authorization: token } });
        } catch (e: any) {
          message.warning('Despesa criada, mas o anexo falhou: ' + (e.message || ''));
        }
      }

      message.success(nLanc > 1
        ? `${nLanc} lançamento(s) criado(s)${form.modo === 'recorrente' ? ' (recorrência mensal)' : ' (parcelado)'}.`
        : 'Despesa criada.');
      onCriou(ids);
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.response?.data?.erro || e.message));
    } finally { setSalvando(false); }
  }

  async function recarregarCentrosCusto(novoCodigo?: string) {
    const token = await getToken();
    const r = await axios.get(`${API_URL}/centros-custo?status=todos&simples=1`,
      { headers: { Authorization: token } });
    const d = r.data.body ? JSON.parse(r.data.body) : r.data;
    setCentrosCusto(Array.isArray(d) ? d : []);
    if (novoCodigo) setForm(f => ({ ...f, centro_custo: novoCodigo }));
  }

  // Categoria semeada por outra tela chega sem o grupo da DRE. Sem grupo o
  // lançamento cai no balde NAO_MAPEADO do relatório — foi o que aconteceu
  // com o reembolso (31/08/2026, 53 lançamentos corrigidos por backfill).
  useEffect(() => {
    if (!form.categoria_dre || form.grupo_dre || !categoriasDre.length) return;
    const c = categoriasDre.find((x: any) => x.linha_dre === form.categoria_dre);
    if (c?.grupo_dre) setForm(f => ({ ...f, grupo_dre: c.grupo_dre }));
  }, [categoriasDre, form.categoria_dre, form.grupo_dre]);

  // Pedido com contrato: acha o centro de custo do contrato. A régua está em
  // `acharCentroDoPedido` (testada fora do componente): por identidade do
  // contrato primeiro, dígitos do número só para pedido antigo, e empate nunca
  // vira chute. Nunca sobrescreve escolha da pessoa.
  useEffect(() => {
    if (!centrosCusto.length) return;
    if (!inicial?.centro_custo_contrato_id && !inicial?.centro_custo_dica) return;
    const achado = acharCentroDoPedido(centrosCusto, {
      contratoId: inicial?.centro_custo_contrato_id,
      dica: inicial?.centro_custo_dica,
    });
    if (!achado) return;
    setForm(f => (f.centro_custo ? f : { ...f, centro_custo: achado }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centrosCusto, inicial?.centro_custo_contrato_id, inicial?.centro_custo_dica]);

  // Fornecedor sugerido pelo pedido. O id foi gravado quando o pedido foi
  // aberto e pode ter envelhecido: se não está mais na lista do cadastro
  // (desativado, mesclado, apagado), SOLTA o id — o aviso "o pedido informou"
  // volta sozinho e a pessoa escolhe. Lançar com id morto estoura no servidor.
  // Só vale para o id que veio do PEDIDO: some quando alguém escolhe outro.
  const [idDoPedidoSumiu, setIdDoPedidoSumiu] = useState(false);
  const idDoPedido = inicial?.fornecedor_id;
  useEffect(() => {
    if (!fornecedores.length) return;
    // A decisão é tomada FORA do updater: updater de setState tem que ser puro
    // (em StrictMode roda duas vezes, e um setState lá dentro dispara junto).
    const sumiu = !!idDoPedido && !fornecedores.some((x: any) => x.id === idDoPedido);
    setIdDoPedidoSumiu(sumiu);
    if (sumiu) {
      // O aviso "o pedido informou: X" volta sozinho e a pessoa escolhe. Lançar
      // com id morto estoura no servidor com erro cru de chave estrangeira.
      setForm(f => (f.fornecedor_id === idDoPedido ? { ...f, fornecedor_id: '' } : f));
      return;
    }
    // Sem id, mas o pedido informou CNPJ: casa pelos dígitos, e só quando o
    // casamento é único — mesma disciplina do centro de custo.
    const dig = String(inicial?.fornecedor_cnpj || '').replace(/[^0-9]/g, '');
    if (dig.length !== 11 && dig.length !== 14) return;
    const casa = fornecedores.filter((x: any) =>
      String(x.cnpj_cpf || '').replace(/[^0-9]/g, '') === dig);
    if (casa.length === 1) setForm(f => (f.fornecedor_id ? f : { ...f, fornecedor_id: casa[0].id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fornecedores, idDoPedido, inicial?.fornecedor_cnpj]);

  async function recarregarFornecedores(novoId?: string) {
    const token = await getToken();
    const r = await axios.get(`${API_URL}/fornecedores?ativo=true&limit=2000`,
      { headers: { Authorization: token } });
    const d = r.data.body ? JSON.parse(r.data.body) : r.data;
    setFornecedores(Array.isArray(d) ? d : []);
    if (novoId) setForm(f => ({ ...f, fornecedor_id: novoId }));
  }

  return (
    <Modal open onCancel={onFechar} onOk={salvar} confirmLoading={salvando} width={760} destroyOnClose
      centered styles={{ body: { maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' } }}
      title="+ Nova despesa" okText="Salvar" cancelText="Cancelar">
      <Form layout="vertical" size="small">
        {/* Pedido com mais de um contrato: o centro de custo fica VAZIO de
            propósito (semear o primeiro jogaria 100% do valor nele). O aviso
            fica aqui em cima porque dentro das observações, no fim do modal,
            é lido na diagonal — e o resultado silencioso é a despesa entrar
            sem contrato nenhum e sumir da DRE dos dois. */}
        {!!inicial?.centros_custo_pedido?.length && !form.centro_custo && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }}
            message="Este pedido é de mais de um contrato"
            description={`Escolha o centro de custo ou divida depois pelo rateio — ${inicial.centros_custo_pedido.join(' + ')}`} />
        )}
        {idDoPedidoSumiu && !form.fornecedor_id && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }}
            message="O fornecedor do pedido não está mais no cadastro"
            description="Ele pode ter sido desativado ou juntado a outro. Escolha o fornecedor de novo antes de salvar." />
        )}
        <Card size="small" title="Informações do lançamento" style={{ marginBottom: 12 }}>
          <Row gutter={10}>
            <Col span={12}>
              <Form.Item label="Fornecedor">
                <Flex gap={4}>
                  <Select
                    value={form.fornecedor_id || undefined}
                    onChange={(v) => {
                      const f = fornecedores.find((x: any) => x.id === v);
                      setForm({ ...form, fornecedor_id: v, fornecedor_original: f?.nome_fantasia || '' });
                    }}
                    placeholder={carregandoForns ? 'Carregando…' : 'Buscar fornecedor'}
                    allowClear showSearch filterOption={filtrarFornecedor}
                    options={opcoesFornecedor(fornecedores, { avisarSemCnpj: true })}
                    style={{ flex: 1, minWidth: 0 }} />
                  <Button size="small" icon={<PlusOutlined />} style={{ flexShrink: 0 }} onClick={() => setNovoFornAberto(true)}
                    title="Cadastrar novo fornecedor" />
                  <Button size="small" icon={<EditOutlined />} style={{ flexShrink: 0 }}
                    disabled={!fornecedorSelecionado}
                    onClick={() => setFornEditando(fornecedorSelecionado)}
                    title={fornecedorSelecionado
                      ? `Editar ${fornecedorSelecionado.nome_fantasia} (CNPJ, razão social…)`
                      : 'Escolha um fornecedor para editar'} />
                </Flex>
                {/* FIN-UI (2026-07-14): texto livre removido — fornecedor só
                    do cadastro (Select acima) ou via botão + (novo fornecedor). */}
                {/* Veio de um ticket: o pedido digitou o prestador em texto
                    livre. Fica à vista até alguém escolher (ou cadastrar) o
                    fornecedor de verdade — senão o nome se perde na passagem. */}
                {!form.fornecedor_id && inicial?.fornecedor_original && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    O pedido informou: <strong>{inicial.fornecedor_original}</strong> — escolha no
                    cadastro ou use o + para cadastrar.
                  </Text>
                )}
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Data de competência" required>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                  value={form.data_competencia ? dayjs(form.data_competencia) : null}
                  onChange={(d) => setForm({ ...form, data_competencia: d ? d.format('YYYY-MM-DD') : '' })} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Valor (R$)" required>
                <Input type="number" step="0.01" min={0} value={form.valor}
                  onChange={e => setForm({ ...form, valor: e.target.value })} placeholder="0,00" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Descrição" required>
                <Input value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })}
                  placeholder="ex: Licença Autodesk Civil 3D" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Categoria">
                <Select value={form.categoria_dre || undefined}
                  onChange={(v) => {
                    const c = categoriasDre.find((c: any) => c.linha_dre === v);
                    setForm({ ...form, categoria_dre: v || '', grupo_dre: c?.grupo_dre || '' });
                  }}
                  placeholder="Selecione…" allowClear showSearch optionFilterProp="label"
                  options={categoriasDre.map((c: any) => ({
                    label: `${c.linha_dre} (${c.grupo_dre})`,
                    value: c.linha_dre,
                  }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Centro de Custo"
                extra="Contratos criam o próprio CC automaticamente; o + cadastra um centro de indiretas.">
                <Flex gap={4}>
                  <Select value={form.centro_custo || undefined}
                    onChange={(v) => setForm({ ...form, centro_custo: v || '' })}
                    placeholder={centrosCusto.length === 0 ? 'Carregando…' : 'Selecione o centro de custo'}
                    allowClear showSearch optionFilterProp="label"
                    loading={centrosCusto.length === 0}
                    options={[...centrosCusto]
                      .sort((a: any, b: any) => ((a.fechado ? 2 : a.tipo === 'estrutural' ? 1 : 0)
                        - (b.fechado ? 2 : b.tipo === 'estrutural' ? 1 : 0)))
                      .map((cc: any) => ({
                        label: `${cc.codigo_canonical}${cc.nome_completo ? ` — ${cc.nome_completo}` : ''}`
                          + (cc.tipo === 'estrutural' ? ' · indireta' : '') + (cc.fechado ? ' · fechado' : ''),
                        value: cc.codigo_canonical,
                      }))}
                    style={{ flex: 1, minWidth: 0 }} />
                  <Button size="small" icon={<PlusOutlined />} style={{ flexShrink: 0 }} onClick={() => setNovoCcAberto(true)}
                    title="Cadastrar novo centro de custo (indireta)" />
                </Flex>
              </Form.Item>
            </Col>
            {(pedeAPessoa(form.categoria_dre) || !!pessoa) && (
              <Col span={12}>
                <PessoaDaEquipe cobrar linha={form.categoria_dre} escolha={pessoa} sugerida={!pessoaMexida && !!pessoa}
                  faltando={pessoaFaltando && !pessoa}
                  fichas={fichas} estado={estadoFichas} recarregar={recarregarFichas}
                  onEscolher={(v) => { setPessoa(v); setPessoaMexida(true); setPessoaFaltando(false); }} />
              </Col>
            )}
          </Row>
        </Card>

        <Card size="small" title="Condição de pagamento" style={{ marginBottom: 12 }}>
          <Row gutter={10}>
            <Col span={24}>
              <Form.Item label="Tipo" style={{ marginBottom: 8 }}>
                <Segmented
                  value={form.modo}
                  onChange={(v) => setForm({ ...form,
                    modo: v as any,
                    parcelas: v === 'avista' ? 1 : (form.parcelas && form.parcelas > 1 ? form.parcelas : 2),
                    intervalo_dias: v === 'recorrente' ? 30 : form.intervalo_dias,
                  })}
                  options={[
                    { label: 'À vista', value: 'avista' },
                    { label: 'Parcelado (divide)', value: 'parcelado' },
                    { label: 'Recorrente (mensal)', value: 'recorrente' },
                  ]} />
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {form.modo === 'parcelado' && 'Divide o valor total em N parcelas (ex.: compra no cartão).'}
                    {form.modo === 'recorrente' && 'Repete o valor cheio a cada mês, X vezes (ex.: salário provisionado).'}
                    {form.modo === 'avista' && 'Um único lançamento.'}
                  </Text>
                </div>
              </Form.Item>
            </Col>
            {form.modo !== 'avista' && (
              <Col span={6}>
                <Form.Item label={form.modo === 'recorrente' ? 'Repetições (meses)' : 'Nº de parcelas'}
                  tooltip={`Digite o número: de 2 a ${MAX_PARCELAS}`
                    + (form.modo === 'recorrente' ? ` meses (${MAX_PARCELAS / 12} anos).` : ' parcelas.')}>
                  {/* Digitado, não mais dropdown. onBlur: campo vazio volta para 2
                      (mínimo de um parcelado/recorrente); o antd já corta acima do max. */}
                  <InputNumber style={{ width: '100%' }} suffix="×"
                    min={2} max={MAX_PARCELAS} step={1} precision={0} placeholder="ex.: 48"
                    value={form.parcelas}
                    onChange={(v) => setForm({ ...form, parcelas: v === null ? null : Math.floor(v) })}
                    onBlur={() => setForm(f => (f.modo !== 'avista' && (f.parcelas === null || f.parcelas < 2)
                      ? { ...f, parcelas: 2 } : f))} />
                </Form.Item>
              </Col>
            )}
            {form.modo === 'parcelado' && !cartaoSelecionado && (
              <Col span={6}>
                <Form.Item label="Intervalo (dias)"
                  tooltip="Mexer aqui passa o cronograma para 'a cada N dias'.">
                  <Input type="number" value={form.intervalo_dias}
                    onChange={e => {
                      const n = parseInt(e.target.value) || 30;
                      setForm({ ...form, intervalo_dias: n });
                      // Sem isto o campo ficava morto: o ritmo nasce 'mensal' e o
                      // cronograma mensal ia por cima. Quem digita 15 aqui quer
                      // quinzenal — e antes do cronograma explícito era o que
                      // acontecia. O default 30 continua caindo no mensal.
                      if (n !== 30) { setRitmo('dias'); setVencManual(false); }
                    }} />
                </Form.Item>
              </Col>
            )}
            {form.modo === 'parcelado' && cartaoSelecionado && (
              <Col span={6}>
                <Form.Item label="Intervalo" >
                  {/* Parcela de cartão cai uma por fatura, sempre no dia do
                      vencimento — de 30 em 30 dias a data desgarra já na 2ª. */}
                  <Input value="uma parcela por fatura" disabled
                    title={`Cada parcela vence no dia ${cartaoSelecionado.dia_vencimento || '?'} do mês seguinte à anterior.`} />
                </Form.Item>
              </Col>
            )}
            <Col span={form.modo !== 'avista' ? 6 : 6}>
              <Form.Item label={form.modo !== 'avista' ? '1º vencimento' : 'Vencimento'} required
                validateStatus={cartaoSelecionado && faturaSugerida
                  && form.data_vencimento === faturaSugerida.vencimento ? 'success' : undefined}
                extra={cartaoSelecionado
                  ? (faturaSugerida
                      ? `💳 ${cartaoSelecionado.nome}: compra em ${diaMes(form.data_competencia)} entra na `
                        + `fatura que fecha ${diaMes(faturaSugerida.fechamento)} e vence ${diaMes(faturaSugerida.vencimento)}.`
                        + (form.data_vencimento !== faturaSugerida.vencimento
                            ? ' (você escolheu outra data — vale a sua)' : '')
                      // faturaSugerida é null por DOIS motivos — culpar o
                      // cadastro com a competência vazia mandaria a Daniele
                      // conferir um cartão que está certo.
                      : !/^\d{4}-\d{2}-\d{2}$/.test(form.data_competencia || '')
                        ? '💳 Informe a data de competência (a data da compra) para o vencimento vir sozinho.'
                        : `⚠️ O cartão ${cartaoSelecionado.nome} está sem dia de fechamento/vencimento `
                          + 'no cadastro — configure em Financeiro › Cartões para o vencimento vir sozinho.')
                  : undefined}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                  value={form.data_vencimento ? dayjs(form.data_vencimento) : null}
                  onChange={(d) => {
                    setVencAuto(false);   // a pessoa mandou — a sugestão não sobrescreve mais
                    // Despesa vinda de pedido com cronograma abre em modo manual,
                    // e aí mexer nesta data — que é OBRIGATÓRIA e está em
                    // destaque — não mudava parcela nenhuma: o servidor ignora
                    // `data_vencimento` quando recebe a lista. Mexeu na âncora,
                    // o cronograma volta ao ritmo (o botão desfaz).
                    setVencManual(false);
                    setForm({ ...form, data_vencimento: d ? d.format('YYYY-MM-DD') : '' });
                  }} />
              </Form.Item>
            </Col>
            {cronogramaVale && (
              <Col span={24}>
                <Form.Item label="Quando vencem" style={{ marginBottom: 8 }}
                  tooltip="Antes o sistema somava 30 dias corridos entre as parcelas, e o dia ia escorregando.">
                  <Flex gap={8} wrap="wrap" align="center">
                    <Segmented size="small" value={ritmo}
                      onChange={(v) => { setRitmo(v as RitmoVencimento); setVencManual(false); }}
                      options={[
                        { label: 'Todo mês no mesmo dia', value: 'mensal' },
                        { label: `A cada ${form.intervalo_dias || 30} dias`, value: 'dias' },
                      ]} />
                    <Text style={{ fontSize: 12 }}>{resumoVencimentos(vencimentos, 5)}</Text>
                    <Button type="link" size="small" style={{ padding: 0, height: 20, fontSize: 12 }}
                      onClick={() => setDatasAbertas(v => !v)}>
                      {datasAbertas ? 'fechar' : 'ajustar datas'}
                    </Button>
                    {vencManual && (
                      <Button type="link" size="small" style={{ padding: 0, height: 20, fontSize: 12 }}
                        onClick={() => setVencManual(false)}>
                        voltar ao ritmo
                      </Button>
                    )}
                  </Flex>
                  {datasAbertas && (
                    <Flex gap={8} wrap="wrap" style={{ marginTop: 8 }}>
                      {vencimentos.map((d, i) => (
                        <div key={i} style={{ width: 132 }}>
                          <Text type="secondary" style={{ fontSize: 11 }}>{i + 1}/{nParcelas}</Text>
                          <DatePicker size="small" style={{ width: '100%' }} format="DD/MM/YYYY"
                            allowClear={false}
                            value={d ? dayjs(d) : null}
                            onChange={(nd) => {
                              // Mexeu numa data: o ritmo para de reescrever a
                              // lista, mas o botão "voltar ao ritmo" desfaz.
                              setVencManual(true);
                              setVencimentos(atual => atual.map((x, k) =>
                                (k === i ? (nd ? nd.format('YYYY-MM-DD') : x) : x)));
                            }} />
                        </div>
                      ))}
                    </Flex>
                  )}
                </Form.Item>
              </Col>
            )}
            {cartaoSelecionado && form.modo !== 'avista' && (
              <Col span={24}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  💳 No cartão quem manda na data é a fatura: cada parcela cai no
                  vencimento do {cartaoSelecionado.nome}.
                </Text>
              </Col>
            )}
            {form.modo !== 'avista' && form.valor && !!form.parcelas && form.parcelas > 1 && (
              <Col span={form.modo === 'recorrente' ? 6 : 24}>
                <Form.Item label=" " colon={false}>
                  <Text style={{ fontSize: 12 }}>
                    {form.modo === 'parcelado'
                      ? `${form.parcelas}× de ${fmt(parseFloat(form.valor) / form.parcelas)} (resto na 1ª)`
                      : `${form.parcelas}× de ${fmt(parseFloat(form.valor))} · total ${fmt(parseFloat(form.valor) * form.parcelas)}`}
                  </Text>
                </Form.Item>
              </Col>
            )}
            <Col span={form.modo !== 'avista' ? 12 : 12}>
              <Form.Item label="Status">
                <Select value={form.status} onChange={(v) => setForm({ ...form, status: v as any })}
                  options={[
                    { label: 'Em aberto', value: 'em_aberto' },
                    { label: 'Agendado', value: 'agendado' },
                    { label: 'Pago', value: 'pago' },
                    { label: 'Cancelado', value: 'cancelado' },
                  ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Forma de pagamento">
                <Select value={form.metodo_pagamento || undefined}
                  onChange={(v) => setForm({ ...form, metodo_pagamento: v || '' })}
                  allowClear placeholder="—"
                  options={OPCOES_FORMA_PAGAMENTO} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Conta de pagamento">
                {/* FZ 5.2: contas do cadastro oficial (manda id + nome); textos
                    soltos do legado CA só como fallback se o cadastro falhar. */}
                <Select
                  value={form.conta_financeira_id || form.conta_financeira || undefined}
                  onChange={(v) => {
                    const cf = contasFin.find((c: any) => c.id === v);
                    // Saindo de um cartão (troca por conta corrente ou limpou):
                    // o que a sugestão preencheu SOZINHA volta atrás — vencimento
                    // que ainda é o da fatura vira a data padrão de novo, e a
                    // forma "Cartão" que entrou automática sai. O que a pessoa
                    // digitou à mão fica.
                    const saindoDoCartao = !!cartaoSelecionado && (!cf || cf.tipo !== 'cartao');
                    const desfaz: any = {};
                    if (saindoDoCartao && faturaSugerida
                        && form.data_vencimento === faturaSugerida.vencimento) {
                      desfaz.data_vencimento = hoje;
                    }
                    if (saindoDoCartao && metodoAuto && form.metodo_pagamento === 'Cartão') {
                      desfaz.metodo_pagamento = '';
                    }
                    setForm({
                      ...form,
                      ...desfaz,
                      conta_financeira_id: cf ? cf.id : '',
                      conta_financeira: cf ? (cf.apelido || cf.nome) : (v || ''),
                    });
                    setVencAuto(true);   // trocar de conta re-arma a sugestão de vencimento
                    if (saindoDoCartao) setMetodoAuto(false);
                  }}
                  allowClear showSearch optionFilterProp="label" placeholder="—"
                  options={contasFin.length
                    ? contasFin.map((c: any) => ({ label: c.apelido || c.nome, value: c.id }))
                    : contasDisponiveis.map(c => ({ label: c, value: c }))} />
              </Form.Item>
            </Col>
            {form.status === 'pago' && (
              <Col span={12}>
                <Form.Item label="Data do pagamento">
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                    value={form.data_pagamento ? dayjs(form.data_pagamento) : null}
                    onChange={(d) => setForm({ ...form, data_pagamento: d ? d.format('YYYY-MM-DD') : '' })} />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Card>

        <Card size="small" title="Anexo (recibo/NF)" style={{ marginBottom: 12 }}>
          <Upload.Dragger
            fileList={anexos}
            beforeUpload={() => false}  // não sobe agora — segura até salvar (usa o id da despesa)
            onChange={({ fileList }) => setAnexos(fileList.slice(-1))}  // 1 arquivo
            onRemove={() => setAnexos([])}
            maxCount={1}
            style={{ padding: '8px 0' }}>
            <p style={{ margin: 0, fontSize: 13 }}><PaperClipOutlined /> Clique ou arraste o recibo/NF (opcional)</p>
            <p style={{ margin: '2px 0 0', fontSize: 11, color: '#999' }}>Anexado ao 1º lançamento (parcela 1) ao salvar.</p>
          </Upload.Dragger>
        </Card>

        <Card size="small" title="Observações">
          <Form.Item style={{ marginBottom: 0 }}>
            <Input.TextArea value={form.observacoes} onChange={e => setForm({ ...form, observacoes: e.target.value })} rows={3}
              placeholder="Anotações livres sobre o lançamento" />
          </Form.Item>
        </Card>
      </Form>

      {/* Sub-modal: cadastro rápido de fornecedor */}
      {novoFornAberto && (
        <ModalNovoFornecedor
          onFechar={() => setNovoFornAberto(false)}
          onCriou={(id) => { setNovoFornAberto(false); recarregarFornecedores(id); }} />
      )}
      {fornEditando && (
        <ModalNovoFornecedor
          fornecedor={fornEditando}
          onFechar={() => setFornEditando(null)}
          onCriou={(id) => { setFornEditando(null); recarregarFornecedores(id || form.fornecedor_id); }} />
      )}
      {/* Sub-modal: cadastro rápido de centro de custo indireto */}
      {novoCcAberto && (
        <ModalNovoCentroCusto
          onFechar={() => setNovoCcAberto(false)}
          onCriou={(codigo) => { setNovoCcAberto(false); recarregarCentrosCusto(codigo); }} />
      )}
    </Modal>
  );
}
